import React, { useState, useEffect } from "react";
import { CalendarIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";

export const EditTaskForm = ({ task, sites, studies, users, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignee: '',
    sliding_date: '',
    priority: 'medium',
    status: 'todo',
    site_id: '',
    study_id: '',
    patient_id: '',
    visit: ''
  });

  useEffect(() => {
    if (task) {
      let slidingDateValue = '';
      if (task.sliding_date) {
        try {
          const date = new Date(task.sliding_date);
          slidingDateValue = date.toISOString().slice(0, 16);
        } catch {
          
          slidingDateValue = '';
        }
      }
      
      setFormData({
        title: task.title || '',
        description: task.description || '',
        assignee: task.assignee || '',
        sliding_date: slidingDateValue,
        priority: task.priority || 'medium',
        status: task.status || 'todo',
        site_id: task.site_id || '',
        study_id: task.study_id || '',
        patient_id: task.patient_id || '',
        visit: task.visit || ''
      });
    }
  }, [task]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let processedSlidingDate = null;
    if (formData.sliding_date && formData.sliding_date.trim()) {
      try {
        let dateStr = formData.sliding_date.trim();
        if (dateStr.includes('T') && (dateStr.includes('Z') || dateStr.includes('+'))) {
          processedSlidingDate = dateStr;
        } else {
          const dateObj = new Date(dateStr);
          if (isNaN(dateObj.getTime())) {
            throw new Error('Invalid date');
          }
          dateObj.setHours(12, 0, 0, 0);
          processedSlidingDate = dateObj.toISOString();
        }
      } catch (error) {
        
        toast.error("Invalid sliding date format");
        return;
      }
    }
    
    const updateData = {
      title: formData.title || task.title,
      description: formData.description || task.description,
      assignee: formData.assignee || task.assignee,
      priority: formData.priority || task.priority,
      site_id: formData.site_id || task.site_id,
      study_id: formData.study_id || task.study_id,
      status: formData.status || task.status
    };
    
    if (processedSlidingDate) {
      updateData.sliding_date = processedSlidingDate;
    }
    
    onSave(task.id, updateData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!task) return null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="title">Task Title</Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Enter task title"
            required
          />
        </div>
        <div>
          <Label htmlFor="assignee">Assignee</Label>
          <Select value={formData.assignee} onValueChange={(value) => handleChange('assignee', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select assignee" />
            </SelectTrigger>
            <SelectContent>
              {users.map((user, index) => (
                <SelectItem key={`assignee-${user.id}-${index}`} value={user.name}>{user.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Enter task description"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="site">Site</Label>
          <Select value={formData.site_id} onValueChange={(value) => handleChange('site_id', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((site, index) => (
                <SelectItem key={`site-${site.id}-${index}`} value={site.id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="study">Study</Label>
          <Select value={formData.study_id} onValueChange={(value) => handleChange('study_id', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select study" />
            </SelectTrigger>
            <SelectContent>
              {studies.map(study => (
                <SelectItem key={study.id} value={study.id}>{study.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="visit">Visit</Label>
        <Input
          id="visit"
          name="visit"
          value={formData.visit}
          onChange={(e) => handleChange('visit', e.target.value)}
          placeholder="Enter visit information (optional)"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="priority">Priority</Label>
          <Select value={formData.priority} onValueChange={(value) => handleChange('priority', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="status">Status</Label>
          <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todo">To Do</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="delayed">Delayed</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="patient_id">Patient ID (optional)</Label>
          <Input
            id="patient_id"
            value={formData.patient_id}
            onChange={(e) => handleChange('patient_id', e.target.value)}
            placeholder="Enter patient ID if applicable"
          />
        </div>
      </div>

      {/* Three Date System */}
      <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
        <h4 className="font-semibold text-gray-900">Task Dates</h4>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="planned_date">Planned Date (Read-only)</Label>
            <Input
              id="planned_date"
              name="planned_date"
              type="datetime-local"
              value={task?.planned_date ? new Date(task.planned_date).toISOString().slice(0, 16) : ''}
              disabled
              className="bg-gray-100 cursor-not-allowed"
            />
            <p className="text-xs text-gray-600 mt-1">Set when task was created</p>
          </div>
          <div>
            <Label htmlFor="sliding_date_edit">Sliding Date (Editable)</Label>
            <p className="text-xs text-gray-600 mb-1">Current date that can be adjusted</p>
            
            <div className="flex items-center space-x-2">
              <Input
                name="sliding_date_visible"
                type="date"
                value={formData.sliding_date ? formData.sliding_date.split('T')[0] : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const newDateTime = new Date(e.target.value);
                    newDateTime.setHours(12, 0, 0, 0);
                    handleChange('sliding_date', newDateTime.toISOString());
                  } else {
                    handleChange('sliding_date', '');
                  }
                }}
                className="flex-1 border-blue-300 focus:border-blue-500 bg-white"
                required
              />
              <CalendarIcon className="h-4 w-4 text-gray-500" />
            </div>
            
            <Input
              id="sliding_date_edit"
              name="sliding_date_hidden"
              type="datetime-local"
              value={formData.sliding_date || ''}
              onChange={(e) => handleChange('sliding_date', e.target.value)}
              className="hidden"
            />
            
            <p className="text-xs text-green-600 mt-1">
              {formData.sliding_date ? 
                `Selected: ${new Date(formData.sliding_date).toLocaleDateString()}` : 
                'No date selected'
              }
            </p>
            
            <p className="text-xs text-gray-600 mt-1">
              Can be adjusted as needed (uses browser's native date picker)
            </p>
          </div>
          <div>
            <Label htmlFor="actual_completion_date">Actual Completion (Auto-set)</Label>
            <Input
              id="actual_completion_date"
              name="actual_completion_date"
              type="datetime-local"
              value={task?.actual_completion_date ? new Date(task.actual_completion_date).toISOString().slice(0, 16) : ''}
              disabled
              className="bg-gray-100 cursor-not-allowed"
            />
            <p className="text-xs text-gray-600 mt-1">Set when task is completed</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Save Changes
        </Button>
      </div>
    </form>
  );
};
